import { apiClient } from '@/lib/apiClient';
import { LeadAssignment, User, AssignmentRule } from '@/types/lead';
import { updateLead } from '@/services/api/leads';
import { fetchMinimalUsers } from '@/hooks/useUsers';

export interface AssignLeadRequest {
  lead_id: number;
  assigned_to: number;
  assigned_by: number;
  reason?: string;
}

export interface AssignmentHistory {
  id: number;
  lead_id: number;
  assigned_to: number;
  assigned_by: number;
  assigned_at: string;
  unassigned_at?: string | null;
  reason?: string;
  is_active: number;
  created_at: string;
  updated_at: string;
  assignee_name: string;
  assigner_name: string;
}

class LeadAssignmentService {
  // Assign lead to user
  async assignLead(data: AssignLeadRequest): Promise<{ success: boolean; assignment?: LeadAssignment; error?: string }> {
    try {
      const result = await apiClient.post(`/leads/${data.lead_id}/assign`, {
        assigned_to: data.assigned_to,
        assigned_by: data.assigned_by,
        assigned_at: new Date().toISOString(),
        reason: data.reason
      });

      return result;
    } catch (error) {
      console.warn('Assignment API failed, trying fallback method:', error);
      
      // Fallback: Use the regular lead update API to set assignment fields
      try {
        const currentTime = new Date().toISOString();
        
        await updateLead(String(data.lead_id), {
          assigned_to: data.assigned_to,
          assigned_by: data.assigned_by,
          assigned_at: currentTime,
        });
        
        console.log('✅ Lead assignment updated successfully via fallback method');
        
        return {
          success: true,
          assignment: {
            id: Date.now(),
            lead_id: data.lead_id,
            assigned_to: data.assigned_to,
            assigned_by: data.assigned_by,
            assigned_at: currentTime,
            reason: data.reason,
            is_active: true
          }
        };
      } catch (fallbackError) {
        console.error('Both assignment methods failed:', fallbackError);
        return { success: false, error: 'Failed to assign lead using both primary and fallback methods' };
      }
    }
  }

  // Get available users for assignment
  async getAvailableUsers(): Promise<User[]> {
    try {
      const data = await fetchMinimalUsers();

      return (Array.isArray(data) ? data : [])
        .filter((user: any) => user.is_active === 1 || user.is_active === true)
        .map((user: any) => ({
          id: user.id,
          name: user.full_name || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username),
          email: '',
          role: user.position || 'User',
          department: 'General'
        }));
    } catch (error) {
      console.error('Error fetching RBAC minimal users:', error);
      return this.getFallbackUsers();
    }
  }
  
  private getFallbackUsers(): User[] {
    return [
      { id: 1, name: "Sarah Johnson", email: "sarah@company.com", role: "Sales Manager", department: "Sales" },
      { id: 2, name: "Mike Chen", email: "mike@company.com", role: "Sales Rep", department: "Sales" },
      { id: 3, name: "Emma Davis", email: "emma@company.com", role: "Account Executive", department: "Sales" },
      { id: 4, name: "James Wilson", email: "james@company.com", role: "Senior Sales Rep", department: "Sales" },
      { id: 5, name: "Lisa Martinez", email: "lisa@company.com", role: "Business Development", department: "Sales" },
    ];
  }

  // Get assignment history for a lead
  async getAssignmentHistory(leadId: number): Promise<AssignmentHistory[]> {
    try {
      const data = await apiClient.get(`/leads/${leadId}/assignment/history`);
      return Array.isArray(data) ? data : data.history || [];
    } catch (error) {
      console.error('Error fetching assignment history:', error);
      return [];
    }
  }

  // Unassign lead
  async unassignLead(leadId: number, unassignedBy: number, reason?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await apiClient.post(`/leads/${leadId}/unassign`, { 
        unassigned_by: unassignedBy, 
        reason 
      });

      return result;
    } catch (error) {
      console.warn('Unassign API failed, trying fallback method:', error);
      
      try {
        await updateLead(String(leadId), {
          assigned_to: null,
          assigned_by: null,
          assigned_at: null,
        });
        
        console.log('✅ Lead unassignment updated successfully via fallback method');
        return { success: true };
      } catch (fallbackError) {
        console.error('Both unassignment methods failed:', fallbackError);
        return { success: false, error: 'Failed to unassign lead using both primary and fallback methods' };
      }
    }
  }

  // Get leads assigned to user
  async getAssignedLeads(userId: number, page = 1, limit = 10): Promise<{ leads: any[]; total: number; totalPages: number }> {
    try {
      const data = await apiClient.get(`/leads/assignment/user/${userId}?page=${page}&limit=${limit}`);
      return {
        leads: data.leads || [],
        total: data.total || 0,
        totalPages: data.totalPages || 1
      };
    } catch (error) {
      console.error('Error fetching assigned leads:', error);
      return { leads: [], total: 0, totalPages: 1 };
    }
  }

  // Get leads assigned to user in a specific month
  async getAssignedLeadsByMonth(userId: number, month: string): Promise<{ leads: any[]; total: number }> {
    try {
      const data = await apiClient.get(`/leads/assignment/user/${userId}/month/${month}`);
      return {
        leads: data.leads || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('Error fetching assigned leads by month:', error);
      return { leads: [], total: 0 };
    }
  }

  // Get leads created by user in a specific month
  async getCreatedLeadsByMonth(userId: number, month: string): Promise<{ leads: any[]; total: number }> {
    try {
      const data = await apiClient.get(`/leads/created/user/${userId}/month/${month}`);
      return {
        leads: data.leads || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('Error fetching created leads by month:', error);
      return { leads: [], total: 0 };
    }
  }

  // Get leads created by user in a specific date range
  async getCreatedLeadsByDateRange(userId: number, startDate: string, endDate: string): Promise<{ leads: any[]; total: number }> {
    const endpoints = [
      `/leads/generated_by_user/${userId}?start_date=${startDate}&end_date=${endDate}`,
      `/leads/created/user/${userId}/range?start_date=${startDate}&end_date=${endDate}`,
      `/leads/created/user/${userId}/range?from_date=${startDate}&to_date=${endDate}`,
    ];

    for (const endpoint of endpoints) {
      try {
        const data = await apiClient.get(endpoint);
        
        // Handle the actual API response format
        if (Array.isArray(data) && data.length > 0 && data[0].generated_leads !== undefined) {
          return { leads: [], total: data[0].generated_leads };
        }
        
        return { leads: data.leads || [], total: data.total || 0 };
      } catch (e) {
        console.warn(`CreatedLeadsByDateRange failed for ${endpoint}:`, e);
      }
    }

    console.error('All attempts failed for getCreatedLeadsByDateRange');
    return { leads: [], total: 0 };
  }

  // Get leads assigned to user in a specific date range
  async getAssignedLeadsByDateRange(userId: number, startDate: string, endDate: string): Promise<{ leads: any[]; total: number }> {
    const endpoints = [
      `/leads/assigned_to_user/${userId}?start_date=${startDate}&end_date=${endDate}`,
      `/leads/assignment/user/${userId}/range?start_date=${startDate}&end_date=${endDate}`,
      `/leads/assignment/user/${userId}/range?from_date=${startDate}&to_date=${endDate}`,
    ];

    for (const endpoint of endpoints) {
      try {
        const data = await apiClient.get(endpoint);
        
        if (Array.isArray(data) && data.length > 0 && data[0].assigned_leads !== undefined) {
          return { leads: [], total: data[0].assigned_leads };
        }
        
        return { leads: data.leads || [], total: data.total || 0 };
      } catch (e) {
        console.warn(`AssignedLeadsByDateRange failed for ${endpoint}:`, e);
      }
    }

    console.error('All attempts failed for getAssignedLeadsByDateRange');
    return { leads: [], total: 0 };
  }

  // Get assignment rules
  async getAssignmentRules(): Promise<AssignmentRule[]> {
    try {
      const data = await apiClient.get('/leads/assignment-rules');
      return data.rules || [];
    } catch (error) {
      console.error('Error fetching assignment rules:', error);
      return [];
    }
  }

  // Auto-assign lead based on rules
  async autoAssignLead(leadId: number, assignedBy: number): Promise<{ success: boolean; assignment?: LeadAssignment; error?: string }> {
    try {
      return await apiClient.post(`/leads/${leadId}/assignment/auto`, { assigned_by: assignedBy });
    } catch (error) {
      console.error('Error auto-assigning lead:', error);
      return { success: false, error: 'Failed to auto-assign lead' };
    }
  }

  // Get assignment statistics
  async getAssignmentStats(): Promise<{
    totalAssigned: number;
    unassigned: number;
    userAssignments: { user_id: number; user_name: string; count: number }[];
  }> {
    try {
      const data = await apiClient.get('/leads/assignment/stats');
      return {
        totalAssigned: data.totalAssigned || 0,
        unassigned: data.unassigned || 0,
        userAssignments: data.userAssignments || []
      };
    } catch (error) {
      console.error('Error fetching assignment stats:', error);
      return { totalAssigned: 0, unassigned: 0, userAssignments: [] };
    }
  }
}

export const leadAssignmentService = new LeadAssignmentService();
